import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import { useApiClient } from "./ApiContext";
import { ApiError, unwrap } from "./client";
import { queryKeys } from "./hooks";
import { isServerAnsweredFailure } from "./queryClient";

/**
 * Simulated-patient runs: Reliv's built-in patient simulator drives a
 * conversation against a frozen candidate on the Project's preview target.
 *
 * THREE REQUESTS, all on the Project's own preview family:
 *
 *  - `GET  …/preview-sim-personas` — the choices the target offers;
 *  - `POST …/preview-sim-runs`     — dispatch a run (202, no preview link);
 *  - `GET  …/preview-sim-runs/{previewRunRef}` — body-free status.
 *
 * The browser names a candidate, a persona, a language, a turn budget, the
 * revision it read and an idempotency key. It names no patient, no target and
 * no endpoint: the fixture the session binds to and the target are the
 * service's, resolved from the Project row. A run spends model calls, so the
 * dispatch never retries on its own — `retry: false`, exactly as session
 * creation and replay do — and a DELIBERATE retry under the same key is safe,
 * because the service answers a reserved key without sending again.
 */
export type PreviewSimPersona = components["schemas"]["PreviewSimPersona"];
export type PreviewSimPersonas = components["schemas"]["PreviewSimPersonas"];
export type CreatePreviewSimRunRequest =
  components["schemas"]["CreatePreviewSimRunRequest"];
export type PreviewSimRunCreated =
  components["schemas"]["PreviewSimRunCreated"];
export type PreviewSimRunStatus = components["schemas"]["PreviewSimRunStatus"];
export type PreviewSimTurnStat = components["schemas"]["PreviewSimTurnStat"];

export const simQueryKeys = {
  personas: (projectId: string) =>
    ["projects", projectId, "preview-sim-personas"] as const,
  run: (projectId: string, previewRunRef: string) =>
    ["projects", projectId, "preview-sim-runs", previewRunRef] as const,
};

/** How often a live sim run's status is re-read. */
export const SIM_POLL_INTERVAL_MS = 3_000;

export function usePreviewSimPersonasQuery(
  projectId: string,
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<PreviewSimPersonas> {
  const client = useApiClient();
  return useQuery({
    queryKey: simQueryKeys.personas(projectId),
    async queryFn() {
      return unwrap(
        await client.GET("/v1/projects/{projectId}/preview-sim-personas", {
          params: { path: { projectId } },
        }),
      );
    },
    enabled: enabled && projectId.length > 0,
    // The list is the target's, and a target's deployment can change it.
    staleTime: 0,
    retry: false,
  });
}

export function useCreatePreviewSimRunMutation(
  projectId: string,
): UseMutationResult<
  PreviewSimRunCreated,
  unknown,
  CreatePreviewSimRunRequest
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    async mutationFn(body: CreatePreviewSimRunRequest) {
      return unwrap(
        await client.POST("/v1/projects/{projectId}/preview-sim-runs", {
          params: { path: { projectId } },
          body,
        }),
      );
    },
    // The run may become the qualifying preview the Lab's Accept button reads.
    onSuccess() {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectOverview(projectId),
      });
    },
    retry: false,
  });
}

/** A run that has reached `completed` or `failed` cannot change. */
export function isSimRunTerminal(status: PreviewSimRunStatus): boolean {
  return status.status === "completed" || status.status === "failed";
}

/**
 * The sim run's status, polled until it is terminal.
 *
 * A 404 is the contract's ONE generic answer — for a preview that is not a sim
 * run as much as for an unknown one — so the query reports it as data
 * (`null`) rather than an error: the Insights page asks this of EVERY run to
 * decide whether to show the patient-simulator panel, and "not a sim run" is
 * the ordinary answer there. Every other failure stays an error.
 */
export function usePreviewSimRunQuery(
  projectId: string,
  previewRunRef: string,
  {
    enabled = true,
    pollIntervalMs = SIM_POLL_INTERVAL_MS,
  }: { enabled?: boolean; pollIntervalMs?: number | false } = {},
): UseQueryResult<PreviewSimRunStatus | null> {
  const client = useApiClient();
  return useQuery({
    queryKey: simQueryKeys.run(projectId, previewRunRef),
    async queryFn() {
      try {
        return unwrap(
          await client.GET(
            "/v1/projects/{projectId}/preview-sim-runs/{previewRunRef}",
            { params: { path: { projectId, previewRunRef } } },
          ),
        );
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: enabled && projectId.length > 0 && previewRunRef.length > 0,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchInterval: (query) => {
      if (pollIntervalMs === false || query.state.status !== "success") {
        return false;
      }
      const data = query.state.data;
      return data === null || data === undefined || isSimRunTerminal(data)
        ? false
        : pollIntervalMs;
    },
  });
}

/**
 * Why a dispatch was not answered with a 202, by what came back. Kinds only;
 * the sentences live with the surface that renders them.
 */
export type SimRunFailure =
  /** 409 `stale_preview_target_revision` — the Project was retargeted; reload. */
  | { kind: "stale_target" }
  /** 409 `idempotency_conflict` — this key names a different run. */
  | { kind: "duplicate_command" }
  /** 409 `preview_admission_refused` — contract §8 refused the candidate's posture. */
  | { kind: "admission_refused" }
  /** 422 on `candidateId` — not this Project's, or expired. */
  | { kind: "candidate_unusable" }
  /** 422 on `language` — the target has no synthetic patient in it. */
  | { kind: "language_unavailable" }
  /** 422 `candidate_validator_mismatch` — rebuild the testing version. */
  | { kind: "candidate_validator_mismatch" }
  /** 422 `preview_target_not_enabled` — the target has no credential. */
  | { kind: "preview_target" }
  | { kind: "denied" }
  /**
   * 502/503/504 or no answer: WHETHER A RUN STARTED IS UNKNOWN. Retry with the
   * SAME key, which cannot start a second one.
   */
  | { kind: "unknown_dispatch" }
  | { kind: "error"; code: string };

export function classifySimRunFailure(error: unknown): SimRunFailure {
  if (!isServerAnsweredFailure(error) || !(error instanceof ApiError)) {
    return { kind: "unknown_dispatch" };
  }
  if (error.code === "preview_admission_refused") {
    return { kind: "admission_refused" };
  }
  if (error.code === "candidate_validator_mismatch") {
    return { kind: "candidate_validator_mismatch" };
  }
  if (error.code === "idempotency_conflict") {
    return { kind: "duplicate_command" };
  }
  if (error.code === "stale_preview_target_revision") {
    return { kind: "stale_target" };
  }
  if (error.code === "preview_target_not_enabled") {
    return { kind: "preview_target" };
  }
  if (error.isForbidden || error.isUnauthenticated) {
    return { kind: "denied" };
  }
  if (error.status === 422) {
    return error.envelope["field"] === "language"
      ? { kind: "language_unavailable" }
      : { kind: "candidate_unusable" };
  }
  if (error.status === 502 || error.status === 503 || error.status === 504) {
    return { kind: "unknown_dispatch" };
  }
  return { kind: "error", code: error.code };
}
